/***************************************************************************
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property } from 'lit/decorators.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { A3dElement, toVectorArray as toVectorArrayConverter } from '../core';
export var A3dTransformErrorNames;
(function (A3dTransformErrorNames) {
    A3dTransformErrorNames["BAD_VECTOR_INPUT"] = "A3dTransformBadVectorInput";
})(A3dTransformErrorNames || (A3dTransformErrorNames = {}));
export const A3dTransformErrorMessages = {
    [A3dTransformErrorNames.BAD_VECTOR_INPUT]: '[TransformElement] Position, rotation and scaling should be in the format "x y z"',
};
const toVectorArray = (value) => {
    return toVectorArrayConverter(value, A3dTransformErrorMessages[A3dTransformErrorNames.BAD_VECTOR_INPUT]);
};
export class TransformElement extends A3dElement {
    constructor() {
        super(...arguments);
        /**
         * Position of the transform element
         * @attr position - x y z position
         */
        this.position = null;
        /**
         * Rotation of the transform element
         * @attr rotation - x y z rotation
         */
        this.rotation = null;
        /**
         * Scaling of the transform element
         * @attr scaling - x y z rotation
         */
        this.scaling = null;
        /**
         * @internal
         */
        this.node3d = null;
    }
    /**
     * @internal
     */
    async _initElement() {
        this.node3d = new TransformNode(this.id, this._scene);
        if (this.parentNode && this.parentNode instanceof TransformElement) {
            this.node3d.parent = this.parentNode.node3d;
        }
        this._updatePosition(this.position);
        this._updateRotation(this.rotation);
        this._updateScaling(this.scaling);
    }
    update(changedProperties) {
        super.update(changedProperties);
        if (!this.node3d) {
            return;
        }
        if (changedProperties.has('position')) {
            this._updatePosition(this.position);
        }
        if (changedProperties.has('rotation')) {
            this._updateRotation(this.rotation);
        }
        if (changedProperties.has('scaling')) {
            this._updateScaling(this.scaling);
        }
    }
    _updatePosition(value) {
        if (!this.node3d || !value) {
            return;
        }
        this.node3d.position = Vector3.FromArray(value);
        this._markSceneMutated();
    }
    _updateRotation(value) {
        if (!this.node3d || !value) {
            return;
        }
        this.node3d.rotation = Vector3.FromArray(value);
        this._markSceneMutated();
    }
    _updateScaling(value) {
        if (!this.node3d || !value) {
            return;
        }
        this.node3d.scaling = Vector3.FromArray(value);
        this._markSceneMutated();
    }
    _markSceneMutated() {
        if (!this._viewer)
            return;
        this._viewer.markSceneMutated();
    }
    /**
     * Return the current transform information
     */
    get transformInfo() {
        return {
            position: this.node3d?.position,
            rotation: this.node3d?.rotation,
            scaling: this.node3d?.scaling,
        };
    }
}
__decorate([
    property({ converter: toVectorArray })
], TransformElement.prototype, "position", void 0);
__decorate([
    property({ converter: toVectorArray })
], TransformElement.prototype, "rotation", void 0);
__decorate([
    property({ converter: toVectorArray })
], TransformElement.prototype, "scaling", void 0);
